<template>
  <div class="user-search">
    <!-- Buscador -->
    <div v-if="!selectedUser.id" class="d-flex align-items-center">
      <b-input-group class="flex-grow-1">
        <b-input
          v-model="searchTerm"
          autofocus
          :disabled="disabledInputs"
          placeholder="Buscar por nombre, apellido o correo"
          @keydown.enter.prevent="searchUser"
        />
        <b-input-group-append>
          <b-button variant="outline-secondary" :disabled="searching || disabledInputs" @click="searchUser">
            <font-awesome-icon v-if="searching" :icon="['fas', 'spinner']" pulse fixed-width />
            <font-awesome-icon v-else :icon="['fas', 'search']" fixed-width />
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <b-button
        v-b-tooltip.hover
        title="Limpiar búsqueda"
        variant="outline-secondary"
        class="ml-2"
        :disabled="searching || disabledInputs || (!searchTerm && !results.length)"
        @click="resetSearch"
      >
        <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
      </b-button>
    </div>

    <!-- Usuario seleccionado -->
    <div v-else class="selected-user">
      <NameInitialsIcon :user="selectedUser" color="#2AAA61" />
      <b-button
        v-b-tooltip.hover
        title="Quitar usuario"
        variant="link"
        class="remove-btn text-danger"
        @click="resetSearch"
      >
        <font-awesome-icon :icon="['far', 'times']" fixed-width size="lg" />
      </b-button>
    </div>

    <!-- Resultados -->
    <div v-if="!selectedUser.id && !disabledInputs" class="results-box mt-2">
      <b-overlay :show="searching && users.length > 0" opacity="0.6" spinner-small>
        <b-list-group v-if="users.length" flush>
          <b-list-group-item
            v-for="user in users"
            :key="user.id"
            tag="div"
            role="button"
            tabindex="0"
            class="user-option"
            @click="selectUser(user)"
            @keydown.enter.prevent="selectUser(user)"
          >
            <NameInitialsIcon :user="user" color="#1C7BFF" />
          </b-list-group-item>
        </b-list-group>

        <!-- Estados vacíos -->
        <div v-else class="empty-state">
          <template v-if="searching">
            <b-spinner small type="grow" class="text-info" />
            <span class="pl-2 text-info">Buscando...</span>
          </template>
          <span v-else-if="searchEmpty" class="text-danger">No hay resultados</span>
          <span v-else class="text-muted">Ingrese al menos 2 caracteres y presione Enter</span>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import NameInitialsIcon from '../../components/shared/NameInitialsIcon'

export default {
  name: 'UserSearchInput',
  components: { NameInitialsIcon },
  props: {
    disabledInputs: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      searchTerm: '',
      searching: false,
      searchEmpty: false,
      results: [],
      selectedUser: {}
    }
  },
  watch: {
    userId (newVal, _oldVal) {
      if (!newVal) this.resetState()
    }
  },
  computed: {
    users () {
      return [...this.results].sort((a, b) =>
        a.first_name.localeCompare(b.first_name, 'es')
      )
    }
  },
  methods: {
    searchUser () {
      if (!this.searchTerm || this.searchTerm.trim().length < 2) {
        this.results = []
        this.searchEmpty = false
        return
      }
      if (this.searching) return
      const term = this.normalizeString(this.searchTerm)
      this.searching = true
      this.$http.get('/api/human_resources/users/advanced_search', {
          params: {
            q: term
          }
        })
        .then(response => {
          this.results = response.data
          this.searchEmpty = this.results.length === 0
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.searching = false
        })
    },
    resetState () {
      this.searchTerm = ''
      this.results = []
      this.searchEmpty = false
      this.selectedUser = {}
    },
    resetSearch () {
      this.resetState()
      this.$emit('reset-user')
    },
    normalizeString (str) {
      return str?.toString()
                  .toLowerCase()
                  .trim()
                  .replace(/\s+/g, ' ') // replace multiple spaces with one
                  .replace(/[áÁ]/g, 'a')
                  .replace(/[éÉ]/g, 'e')
                  .replace(/[íÍ]/g, 'i')
                  .replace(/[óÓ]/g, 'o')
                  .replace(/[úÚ]/g, 'u')
    },
    selectUser (user) {
      this.selectedUser = user
      this.$emit('set-user', user)
    }
  }
}
</script>

<style scoped>
/* Usuario seleccionado: tarjeta compacta con botón para quitar */
.selected-user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.5rem 0.4rem 0.75rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.remove-btn {
  padding: 0.25rem 0.4rem;
  line-height: 1;
}

.remove-btn:hover,
.remove-btn:focus {
  background-color: rgba(220, 53, 69, 0.1);
  text-decoration: none;
}

/* Caja de resultados con scroll */
.results-box {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #fff;
}

.user-option {
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.1s ease-in-out;
}

.user-option:hover,
.user-option:focus {
  background-color: #eef4ff;
  outline: none;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0.75rem;
  font-size: 0.9rem;
  text-align: center;
}
</style>
